import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppearanceProvider } from "@eduviaos/ui-kit";
import { AuthProvider } from "@/auth/AuthProvider";
import { RequireAuth } from "@/auth/RequireAuth";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { TenantsPage } from "@/pages/TenantsPage";
import { TenantDetailPage } from "@/pages/TenantDetailPage";
import { TenantFormEditorPage } from "@/pages/TenantFormEditorPage";
import { EmailTemplatesListPage } from "@/pages/EmailTemplatesListPage";
import { EmailTemplateEditorPage } from "@/pages/EmailTemplateEditorPage";
import { AssessmentTemplatesLibraryPage } from "@/pages/AssessmentTemplatesLibraryPage";
import { SupportInboxPage } from "@/pages/SupportInboxPage";
import { AppShell } from "@/components/AppShell";
import { PlatformRazorpayPage } from "@/pages/PlatformRazorpayPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // The cookie-refresh dance handles 401s transparently; let
      // TanStack Query treat data as fresh for 30s to avoid double-
      // fetches on tab refocus during dev.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <AppearanceProvider defaultPalette="slate-pro">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route path="/settings/email-templates" element={<EmailTemplatesListPage />} />
                <Route
                  path="/settings/email-templates/:key"
                  element={<EmailTemplateEditorPage />}
                />
                <Route
                  path="/settings/email-templates/:key/tenants/:slug"
                  element={<EmailTemplateEditorPage />}
                />
                <Route
                  path="/settings/assessment-templates"
                  element={<AssessmentTemplatesLibraryPage />}
                />
                <Route
                  path="/settings"
                  element={<Navigate to="/settings/email-templates" replace />}
                />
                <Route path="/billing" element={<PlatformRazorpayPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/support" element={<SupportInboxPage />} />
                {/* Phase B2.2: SA-level /forms surface removed (forms
                    are tenant-scoped, the editor was orphaned).
                    Old /forms/* URLs redirect to /tenants for back-
                    compat. */}
                <Route path="/forms" element={<Navigate to="/tenants" replace />} />
                <Route path="/forms/*" element={<Navigate to="/tenants" replace />} />
                <Route path="/tenants" element={<TenantsPage />} />
                {/* Reserved tenant slugs intercepted before the `:slug`
                    parameter route so direct nav (or stale bookmarks)
                    don't 404 with "Tenant not found". `/tenants/new`
                    redirects to the list with a query flag the page
                    reads to auto-open the create-tenant dialog. */}
                <Route path="/tenants/new" element={<Navigate to="/tenants?create=1" replace />} />
                <Route path="/tenants/:slug" element={<TenantDetailPage />} />
                <Route
                  path="/tenants/:slug/forms/:formSlug/edit"
                  element={<TenantFormEditorPage />}
                />
                {/* Phase B2.2: Branding is an inline tab on the tenant
                    detail page. The legacy standalone route now redirects
                    so old bookmarks still resolve. */}
                <Route path="/tenants/:slug/branding" element={<TenantBrandingRedirect />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </AppearanceProvider>
  );
}

/**
 * Back-compat redirect from the legacy `/tenants/:slug/branding` route
 * to the inline Branding tab on the tenant detail page (Phase B2.2).
 * Bookmarks, audit log links, and email templates referencing the old
 * URL keep working without a 404.
 */
function TenantBrandingRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/tenants/${slug}?tab=branding`} replace />;
}
